import type { AuthNgSchemaTypes } from "@bfc-software/api-schemas";

// ── API-derived types ──

export type UserInfo = AuthNgSchemaTypes.components["schemas"]["userInfo"];
export type OrgInfo = NonNullable<UserInfo["orgs"]>[number];
export type Warehouse = NonNullable<OrgInfo["warehouses"]>[number];
export type PolicyConfig =
  AuthNgSchemaTypes.paths["/authz/get-policy"]["get"]["responses"][200]["content"]["application/json"];

/** Raw API session response - field names are misleading, use SessionInfo instead */
export type RawSessionData =
  AuthNgSchemaTypes.components["schemas"]["userSession"];

/**
 * Frontend session info with clearer naming.
 * The AAA service returns `tenantId` which is actually a signed JWT, not a tenant ID.
 */
export type SessionInfo = {
  /** JWT signed by AAA service (aaa.bfc.software) for downstream service auth */
  aaaToken?: string;
  /** Dakota session ID (legacy, currently always empty) */
  dakotaSession?: string;
};

/** Map the raw API userSession response to our clearer frontend type */
export function toSessionInfo(raw: RawSessionData): SessionInfo {
  return {
    aaaToken: raw.tenantId ?? undefined,
    dakotaSession: raw.dakota_session ?? undefined,
  };
}

/** @deprecated Use OrgInfo[] directly */
export type Organizations = OrgInfo[];
/** @deprecated Use Warehouse[] directly */
export type Locations = Warehouse[];

// ── Auth status symbols ──

export const UNAUTHENTICATED: unique symbol = Symbol("unauthenticated");
export const REDIRECTING: unique symbol = Symbol("redirecting");
export const LOADING_ORGS: unique symbol = Symbol("loading_orgs");
export const AWAITING_PASSWORD: unique symbol = Symbol("awaiting_password");
export const AUTHENTICATING: unique symbol = Symbol("authenticating");
export const AWAITING_ORG_SELECT: unique symbol = Symbol("awaiting_org_select");
export const EXCHANGING: unique symbol = Symbol("exchanging");
export const CREATING_SESSION: unique symbol = Symbol("creating_session");
export const HYDRATING: unique symbol = Symbol("hydrating");
export const AUTHENTICATED: unique symbol = Symbol("authenticated");
export const AUTH_ERROR: unique symbol = Symbol("auth_error");

export const Status = {
  UNAUTHENTICATED,
  REDIRECTING,
  LOADING_ORGS,
  AWAITING_PASSWORD,
  AUTHENTICATING,
  AWAITING_ORG_SELECT,
  EXCHANGING,
  CREATING_SESSION,
  HYDRATING,
  AUTHENTICATED,
  AUTH_ERROR,
} as const;

// ── CurrentAuthState union ──

type WithOrgs = {
  email: string;
  orgs: OrgInfo[];
  session: string;
};

type WithToken = {
  email: string;
  orgId: string;
  orgs: OrgInfo[];
  token: string;
  userInfo: UserInfo;
};

export type CurrentAuthState =
  // Entry
  | { status: typeof UNAUTHENTICATED }

  // Hydrating from localStorage - have a token but need to refetch full user data
  | {
      status: typeof HYDRATING;
      token: string;
      orgId: string;
      /** Null when the stored session had not entered a product yet. */
      locationId: string | null;
    }

  // Magic link branch
  | { status: typeof REDIRECTING; token: string; tokenType: string }

  // Password branch
  | { status: typeof LOADING_ORGS; email: string }
  | {
      status: typeof AWAITING_PASSWORD;
      email: string;
      orgId: string;
      orgs: OrgInfo[];
    }
  | {
      status: typeof AUTHENTICATING;
      email: string;
      orgId: string;
      password: string;
    }

  // Shared (both flows converge here)
  | ({ status: typeof AWAITING_ORG_SELECT } & WithOrgs)
  | { status: typeof EXCHANGING; email: string; orgId: string; session: string }
  /**
   * `locationId` is null until a product asks for a warehouse.
   *
   * Signing in mints a session with no warehouse, because the product picker
   * does not need one - AAA's /authz/exchange-token omits the warehouse claims
   * when none is given. Entering a product that is warehouse-scoped selects one
   * and re-mints through /authz/create-session, so everything inside a product
   * runs on a warehouse-bound session exactly as before.
   */
  | ({ status: typeof CREATING_SESSION; locationId: string | null } & WithToken)
  | ({
      status: typeof AUTHENTICATED;
      locationId: string | null;
      sessionData: SessionInfo;
    } & WithToken)

  // Error
  | { status: typeof AUTH_ERROR; message: string };

// ── AuthAction union (for reducer) ──

export type AuthAction =
  | { type: "START_REDIRECT"; token: string; tokenType: string }
  | { type: "START_LOGIN"; email: string }
  | { type: "ORGS_LOADED"; email: string; orgs: OrgInfo[]; session?: string }
  | { type: "SELECT_ORG"; orgId: string }
  | { type: "SUBMIT_PASSWORD"; password: string }
  | { type: "AUTH_SUCCESS"; userInfo: UserInfo }
  | {
      type: "REFRESH_SUCCESS";
      userInfo: UserInfo;
      locationId: string | null;
      sessionData: SessionInfo;
    }
  | { type: "SELECT_LOCATION"; locationId: string }
  | { type: "SESSION_CREATED"; sessionData: SessionInfo }
  | { type: "ERROR"; message: string }
  | { type: "LOGOUT" }
  | { type: "HYDRATE"; state: CurrentAuthState };
